import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { API_BASE_URL } from '../config';
import { getAdminBadge } from '../utils/adminBadge';
import { isAdminOnly } from '../utils/can.js';
import { useSelectStyles } from '../utils/selectStyles';
import { AGENT_KEYS, agentPresentation } from '../pages/picture-ai/agents';

const fieldClass = 'w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100';
const labelClass = 'block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1';

const emptyGroup = (scope) => ({ scope });

/** Raw enum values as the agents write them, against the sentence shown for each. */
const VALUE_LABELS = {
  analyzed: 'Analyzed', not_analyzed: 'Not analyzed', failed: 'Analysis failed',
  met: 'Met', not_met: 'Not met', not_applicable: 'Not applicable', undetermined: 'Undetermined',
  loyalty_card: 'Loyalty card', immediate_discount: 'Immediate discount',
  mail_in_rebate: 'Mail-in rebate', bundle: 'Bundle', trade_in: 'Trade-in',
  quantity: 'Quantity', coupon: 'Coupon', other: 'Other',
  compliant: 'Compliant', non_compliant: 'Non-compliant',
};

/** A filter with nothing in it is no filter: an empty shell would restrict the gallery to nothing. */
const meaningful = (value) => {
  const groups = (value?.groups || []).filter(group => Object.keys(group).length > 1);
  if (!value?.status && groups.length === 0) return null;
  return { ...(value.status ? { status: value.status } : {}), ...(groups.length ? { groups } : {}) };
};

const countConditions = (value) => (value?.status ? 1 : 0)
  + (value?.groups || []).reduce((total, group) => total + Object.keys(group).length - 1, 0);

/**
 * Text and number inputs that only reach the gallery once the person is done typing.
 *
 * Every other control here is a discrete choice and applies at once; a price would otherwise refetch
 * on each digit, and « 2 » is a very different query from « 2000 ».
 */
const DeferredInput = ({ value, onCommit, type = 'text', ...rest }) => {
  const [draft, setDraft] = useState(value ?? '');
  useEffect(() => { setDraft(value ?? ''); }, [value]);

  const commit = () => {
    if (`${draft}` === `${value ?? ''}`) return;
    onCommit(draft === '' ? null : draft);
  };

  return (
    <input
      {...rest}
      type={type}
      className={fieldClass}
      value={draft}
      onChange={event => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); commit(); } }}
    />
  );
};

/**
 * Le même contrôle que les dimensions ordinaires de la barre : cherchable, et multiple par défaut.
 *
 * Une valeur déjà retenue mais absente des options courantes se réaffiche telle quelle — une
 * référence trouvée par recherche disparaît de la liste dès que le terme change, et elle ne doit
 * pas disparaître du filtre avec elle.
 */
const Choice = ({
  label, value, options, onChange, placeholder,
  isMulti = true, onInputChange, isLoading, styles
}) => {
  const asOption = (raw) => options.find(option => option.value === raw) || { value: raw, label: raw };
  const given = (raw) => raw !== null && raw !== undefined && raw !== '';
  const selected = isMulti
    ? [value].flat().filter(given).map(asOption)
    : (given(value) ? asOption(value) : null);

  return (
    <div>
      <span className={labelClass}>{label}</span>
      <Select
        value={selected}
        options={options}
        onChange={(picked) => onChange(isMulti
          ? (picked?.length ? picked.map(option => option.value) : null)
          : (picked?.value ?? null))}
        onInputChange={onInputChange}
        isMulti={isMulti}
        isClearable
        isLoading={isLoading}
        styles={styles}
        className="text-sm"
        classNamePrefix="select"
        placeholder={placeholder}
        menuPlacement="auto"
        noOptionsMessage={() => null}
      />
    </div>
  );
};

/**
 * The AI analyses, as filters an administrator picks by hand.
 *
 * Deliberately not part of the dimension loop above: those values live in the facets cube, which
 * carries no analysis column, so nothing here can be served by `/api/columns/:column`.
 *
 * Every field of one label block lands on the same label, which is what « a Samsung above 2 000 € »
 * means. Asking for a second product opens a second block, and that is the whole difference.
 */
export default function AiFacetsPanel({ value, onChange, fetchWithAuth }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [vocabulary, setVocabulary] = useState(null);
  const [brands, setBrands] = useState([]);
  const [criteria, setCriteria] = useState([]);
  const [references, setReferences] = useState([]);
  const referenceTimer = useRef(null);

  const active = countConditions(value);

  const request = useCallback(async (path) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/ai-facets${path}`);
    if (!response.ok) throw new Error(`${response.status}`);
    return response.json();
  }, [fetchWithAuth]);

  useEffect(() => {
    if (!open || vocabulary) return;
    request('').then(setVocabulary).catch(() => setVocabulary({ coverage: null }));
    request('/brands').then(setBrands).catch(() => setBrands([]));
    request('/criteria').then(setCriteria).catch(() => setCriteria([]));
  }, [open, vocabulary, request]);

  // References are one value per product on a shelf: the list is never downloaded, only searched.
  const searchReferences = (term) => {
    clearTimeout(referenceTimer.current);
    referenceTimer.current = setTimeout(() => {
      request(`/references?q=${encodeURIComponent(term)}`).then(setReferences).catch(() => setReferences([]));
    }, 250);
  };

  const apply = (next) => onChange(meaningful(next));
  const groups = value?.groups || [];
  const patch = (index, changes) => apply({
    ...value,
    groups: groups.map((group, position) => {
      if (position !== index) return group;
      const merged = { ...group, ...changes };
      Object.keys(changes).forEach(key => { if (changes[key] === null) delete merged[key]; });
      return merged;
    })
  });
  const addGroup = (scope) => apply({ ...value, groups: [...groups, emptyGroup(scope)] });
  const removeGroup = (index) => apply({ ...value, groups: groups.filter((_, position) => position !== index) });

  const picture = groups.findIndex(group => group.scope === 'picture');
  const pictureGroup = picture === -1 ? {} : groups[picture];
  const patchPicture = (changes) => (picture === -1
    ? apply({ ...value, groups: [...groups, { ...emptyGroup('picture'), ...changes }] })
    : patch(picture, changes));

  const coverage = vocabulary?.coverage;
  const naming = (raw) => (VALUE_LABELS[raw] ? t(VALUE_LABELS[raw]) : raw);
  const styles = useSelectStyles();
  const plain = (values = []) => values.map(raw => ({ value: raw, label: raw }));
  const named = (values = []) => values.map(raw => ({ value: raw, label: naming(raw) }));
  const agentOptions = (values = []) => values.map(raw => ({
    value: raw, label: AGENT_KEYS.includes(raw) ? t(agentPresentation(raw).name) : raw
  }));

  // Les choix se resserrent sur l'agent retenu. Proposer un verdict de conformité quand on
  // interroge l'extracteur visuel n'a pas de sens : il n'en produit aucun, et la combinaison ne
  // peut rien rendre. Sans agent retenu, on offre l'union — on ne sait pas encore ce qui est visé.
  const narrowed = useMemo(() => {
    const byAgent = vocabulary?.by_agent || {};
    const chosen = [pictureGroup.agent].flat().filter(Boolean);
    const scope = chosen.length ? chosen.filter(agent => byAgent[agent]) : Object.keys(byAgent);
    const union = (key) => [...new Set(scope.flatMap(agent => byAgent[agent]?.[key] || []))];
    return {
      variants: union('variants'),
      verdicts: union('verdicts'),
      categories: union('categories'),
      tags: union('tags'),
      scopes: new Set(scope.flatMap(agent => byAgent[agent]?.scopes || []))
    };
  }, [vocabulary, pictureGroup.agent]);

  return (
    <div className="mt-4 rounded-lg border border-gray-200 dark:border-gray-700">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-300"
      >
        {isAdminOnly('ai.facets') && getAdminBadge()}
        <span className="flex-1">{t('AI analysis — advanced')}</span>
        {active > 0 && (
          <span className="rounded-full bg-blue-100 px-1.5 text-xs font-semibold text-blue-700 dark:bg-blue-900 dark:text-blue-200">
            {active}
          </span>
        )}
        <span className="text-gray-400">{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div className="space-y-4 border-t border-gray-200 px-3 py-3 dark:border-gray-700">
          {coverage && (
            // Un filtre IA restreint au sous-ensemble analysé : les chiffres le disent, l'infobulle
            // explique. Une phrase à l'écran ne se lisait pas.
            <div
              className="flex flex-wrap gap-1.5 text-xs text-gray-600 dark:text-gray-300"
              title={t('AI filters only reach analyzed photos.')}
            >
              {[
                [coverage.analysed, 'analyzed'],
                [coverage.with_labels, 'labels'],
                [coverage.with_criteria, 'criteria']
              ].map(([count, label]) => (
                <span key={label} className="rounded-full bg-gray-100 px-2 py-0.5 dark:bg-gray-800">
                  <span className="font-semibold tabular-nums">{Number(count || 0).toLocaleString()}</span> {t(label)}
                </span>
              ))}
            </div>
          )}

          {vocabulary && (
            <Choice
              label={t('Analysis status')}
              value={value?.status}
              options={named(vocabulary.analysis_statuses)}
              placeholder={t('Select...')}
              isMulti={false}
              styles={styles}
              onChange={status => apply({ ...value, status })}
            />
          )}

          {vocabulary && (
            <fieldset className="space-y-2">
              <legend className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t('On the photo')}</legend>
              <Choice
                label={t('Agent')} value={pictureGroup.agent} options={agentOptions(vocabulary.agents)}
                placeholder={t('Select...')} styles={styles}
                onChange={agent => patchPicture({ agent, variant: null, verdict: null })}
              />
              {narrowed.variants.length > 0 && (
                <Choice
                  label={t('Variant')} value={pictureGroup.variant} options={plain(narrowed.variants)}
                  placeholder={t('Select...')} styles={styles}
                  onChange={variant => patchPicture({ variant })}
                />
              )}
              {narrowed.verdicts.length > 0 && (
                <Choice
                  label={t('Verdict')} value={pictureGroup.verdict} options={named(narrowed.verdicts)}
                  placeholder={t('Select...')} styles={styles}
                  onChange={verdict => patchPicture({ verdict })}
                />
              )}
              {narrowed.categories.length > 0 && (
                <Choice
                  label={t('Category')} value={pictureGroup.categories} options={named(narrowed.categories)}
                  placeholder={t('Select...')} styles={styles}
                  onChange={categories => patchPicture({ categories })}
                />
              )}
              {narrowed.tags.length > 0 && (
                <Choice
                  label={t('Tags')} value={pictureGroup.tags} options={named(narrowed.tags)}
                  placeholder={t('Select...')} styles={styles}
                  onChange={tags => patchPicture({ tags })}
                />
              )}
              <Choice
                label={t('Observed brand')} value={pictureGroup.brands} styles={styles}
                options={brands.map(brand => ({ value: brand.value, label: `${brand.value} (${brand.pictures})` }))}
                placeholder={t('Select...')} onChange={brandList => patchPicture({ brands: brandList })}
              />
              <Choice
                label={t('Product reference')} value={pictureGroup.references} styles={styles}
                options={references.map(reference => ({ value: reference.value, label: reference.value }))}
                placeholder={t('Type to search...')}
                onInputChange={(term, meta) => { if (meta.action === 'input-change') searchReferences(term); return term; }}
                onChange={referenceList => patchPicture({ references: referenceList })}
              />
            </fieldset>
          )}

          {groups.map((group, index) => group.scope !== 'label' ? null : (
            <fieldset key={`label-${index}`} className="space-y-2 rounded-lg border border-blue-200 bg-blue-50/40 p-2 dark:border-blue-900 dark:bg-blue-950/20">
              <legend className="flex items-center gap-2 px-1 text-xs font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">
                {t('Same label')}
                <button type="button" onClick={() => removeGroup(index)} className="text-[11px] font-normal text-red-600 hover:underline">
                  {t('Remove')}
                </button>
              </legend>
              <p className="px-1 text-[11px] leading-snug text-gray-500 dark:text-gray-400">
                {t('Every field below must be true of one and the same label.')}
              </p>
              <div>
                <span className={labelClass}>{t('Brand')}</span>
                <DeferredInput value={group.brand} onCommit={brand => patch(index, { brand })} list="ai-brand-list" />
              </div>
              <div>
                <span className={labelClass}>{t('Reference')}</span>
                <DeferredInput
                  value={group.reference}
                  onCommit={reference => patch(index, { reference })}
                  onInput={event => searchReferences(event.target.value)}
                  list="ai-reference-list"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <span className={labelClass}>{t('Minimum price')}</span>
                  <DeferredInput
                    type="number" min="0" value={group.price_min_cents == null ? '' : group.price_min_cents / 100}
                    onCommit={euros => patch(index, { price_min_cents: euros === null ? null : Math.round(Number(euros) * 100) })}
                  />
                </div>
                <div>
                  <span className={labelClass}>{t('Maximum price')}</span>
                  <DeferredInput
                    type="number" min="0" value={group.price_max_cents == null ? '' : group.price_max_cents / 100}
                    onCommit={euros => patch(index, { price_max_cents: euros === null ? null : Math.round(Number(euros) * 100) })}
                  />
                </div>
              </div>
              <Choice
                label={t('Currency')} value={group.currency} options={plain(vocabulary?.currencies)}
                placeholder={t('Euro assumed')} isMulti={false} styles={styles}
                onChange={currency => patch(index, { currency })}
              />
              <Choice
                label={t('Energy class')} value={group.energy_class} options={plain(vocabulary?.energy_classes)}
                placeholder={t('Select...')} isMulti={false} styles={styles}
                onChange={energy_class => patch(index, { energy_class })}
              />
              <Choice
                label={t('Promotion')} value={group.promotion_mechanism} options={named(vocabulary?.promotion_mechanisms)}
                placeholder={t('Select...')} isMulti={false} styles={styles}
                onChange={promotion_mechanism => patch(index, { promotion_mechanism })}
              />
              <label className="flex items-center gap-2 px-1 text-xs text-gray-600 dark:text-gray-400">
                <input
                  type="checkbox" checked={group.currency_read === true}
                  onChange={event => patch(index, { currency_read: event.target.checked || null })}
                />
                {t('Only labels whose currency was actually read')}
              </label>
            </fieldset>
          ))}

          {groups.map((group, index) => group.scope !== 'criterion' ? null : (
            <fieldset key={`criterion-${index}`} className="space-y-2 rounded-lg border border-amber-200 bg-amber-50/40 p-2 dark:border-amber-900 dark:bg-amber-950/20">
              <legend className="flex items-center gap-2 px-1 text-xs font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-300">
                {t('Same criterion')}
                <button type="button" onClick={() => removeGroup(index)} className="text-[11px] font-normal text-red-600 hover:underline">
                  {t('Remove')}
                </button>
              </legend>
              <Choice
                label={t('Criterion')} value={group.key} isMulti={false} styles={styles}
                options={criteria.map(entry => ({
                  value: entry.value, label: `${entry.label} — ${entry.configuration_name}`
                }))}
                placeholder={t('Select...')}
                onChange={(key) => {
                  const chosen = criteria.find(entry => entry.value === key);
                  patch(index, { key, configuration_id: chosen?.configuration_id ?? null });
                }}
              />
              <Choice
                label={t('Status')} value={group.status} options={named(vocabulary?.criterion_statuses)}
                placeholder={t('Select...')} styles={styles}
                onChange={status => patch(index, { status })}
              />
            </fieldset>
          ))}

          <div className="flex flex-wrap gap-2">
            {narrowed.scopes.has('label') && (
              <button type="button" onClick={() => addGroup('label')} className="rounded-md border border-dashed border-blue-300 px-2 py-1 text-xs text-blue-700 dark:border-blue-700 dark:text-blue-300">
                + {t('A product')}
              </button>
            )}
            {narrowed.scopes.has('criterion') && (
              <button type="button" onClick={() => addGroup('criterion')} className="rounded-md border border-dashed border-amber-300 px-2 py-1 text-xs text-amber-800 dark:border-amber-700 dark:text-amber-300">
                + {t('A criterion')}
              </button>
            )}
            {active > 0 && (
              <button type="button" onClick={() => onChange(null)} className="ml-auto text-xs text-gray-500 underline">
                {t('Clear')}
              </button>
            )}
          </div>

          <datalist id="ai-brand-list">
            {brands.map(brand => <option key={brand.value} value={brand.value} />)}
          </datalist>
          <datalist id="ai-reference-list">
            {references.map(reference => <option key={reference.value} value={reference.value} />)}
          </datalist>
        </div>
      )}
    </div>
  );
}
